import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, formatApiError } from "../lib/auth";
import { UserPlus, MailCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Post-register success panel (no JWT until verified)
  const [pending, setPending] = useState(null); // { email, email_sent, verification_link_dev }
  const [resendBusy, setResendBusy] = useState(false);
  const [resendInfo, setResendInfo] = useState("");

  if (!loading && user) return <Navigate to="/home" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/register`, {
        email: email.trim(),
        password,
        name: name.trim(),
      });
      setPending(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doResend = async () => {
    if (!pending?.email) return;
    setResendBusy(true);
    setResendInfo("");
    try {
      const { data } = await axios.post(`${API}/auth/resend-verification`, {
        email: pending.email,
      });
      if (data.email_sent) {
        setResendInfo("New verification email sent.");
      } else if (data.verification_link_dev) {
        setResendInfo(`Dev mode — link: ${data.verification_link_dev}`);
        setPending((p) => ({ ...p, verification_link_dev: data.verification_link_dev }));
      } else {
        setResendInfo("If your email is unverified, we've sent a new link.");
      }
    } catch (err) {
      setResendInfo(formatApiError(err));
    } finally {
      setResendBusy(false);
    }
  };

  // ---- Pending verification panel ----
  if (pending) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 to-slate-200">
        <Card className="w-full max-w-md shadow-lg" data-testid="register-pending">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <MailCheck className="h-5 w-5 text-emerald-600" /> Check your email
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p>
              We've sent a verification link to{" "}
              <b className="break-all">{pending.email}</b>. Click the link to activate
              your account, then you'll be signed in automatically.
            </p>
            {!pending.email_sent && pending.verification_link_dev && (
              <div
                className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 break-all"
                data-testid="register-dev-link"
              >
                <b>Dev mode</b> (email service not yet configured) — open this link:
                <br />
                <a href={pending.verification_link_dev} className="underline">
                  {pending.verification_link_dev}
                </a>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={doResend}
              disabled={resendBusy}
              data-testid="register-resend-btn"
            >
              {resendBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Resend verification email
            </Button>
            {resendInfo && (
              <div className="text-xs text-slate-600 break-all" data-testid="register-resend-info">
                {resendInfo}
              </div>
            )}
            <div className="text-center text-sm text-slate-500 pt-2 border-t">
              Already verified?{" "}
              <Link to="/login" className="underline hover:text-slate-800" data-testid="goto-login">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Register form ----
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-50 to-slate-200">
      <Card className="w-full max-w-md shadow-lg" data-testid="register-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <UserPlus className="h-5 w-5" /> Create Account
          </CardTitle>
          <p className="text-sm text-slate-500">ZAMBA Product Selector</p>
          <p className="text-xs text-slate-500 mt-1">
            Use your <b>company email</b>. Free providers (Gmail, Yahoo, Outlook, etc.)
            are not accepted.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name"
              />
            </div>
            <div>
              <Label htmlFor="email">Company Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email"
                placeholder="you@yourcompany.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password"
              />
              <div className="text-xs text-slate-500 mt-1">Min 6 characters.</div>
            </div>
            {error && (
              <div
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded"
                data-testid="register-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="register-submit"
            >
              {busy ? "Creating…" : "Create Account"}
            </Button>
            <div className="text-center text-sm text-slate-600">
              Already have an account?{" "}
              <Link to="/login" className="text-slate-900 underline" data-testid="goto-login">
                Sign in
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
